import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Sparkles, TrendingUp, DollarSign, Users, Target, 
  CheckCircle, ArrowRight, AlertCircle, Lightbulb,
  ChevronLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AIMembershipHelp = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-teal-600 hover:text-teal-700">
                <ChevronLeft className="h-5 w-5" />
                <span className="font-semibold">Home</span>
              </Link>
              <span className="text-gray-300">|</span>
              <Link to="/help" className="flex items-center gap-2 text-teal-600 hover:text-teal-700">
                <ChevronLeft className="h-5 w-5" />
                <span className="font-semibold">Help Center</span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-gray-600 hover:text-gray-900 font-medium">
                Dashboard
              </Link>
              <Button asChild variant="outline">
                <Link to="/admin">Go to App</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">AI-Powered Membership Suggestions</h1>
            <p className="text-gray-600">Generate intelligent membership plans with revenue projections</p>
          </div>
        </div>

        {/* Overview */}
        <Card>
          <CardHeader>
            <CardTitle>Overview</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              The AI Membership Suggestions feature uses advanced artificial intelligence to generate 
              customized membership plan recommendations based on your practice specialty, location, 
              and market positioning. Each suggestion includes comprehensive revenue projections and 
              business intelligence metrics to help you make data-driven decisions.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <Sparkles className="h-6 w-6 text-purple-600 mb-2" />
                <h4 className="font-semibold text-sm text-purple-900">AI-Generated</h4>
                <p className="text-xs text-purple-700 mt-1">Plans created by GPT-4</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <TrendingUp className="h-6 w-6 text-blue-600 mb-2" />
                <h4 className="font-semibold text-sm text-blue-900">Revenue Insights</h4>
                <p className="text-xs text-blue-700 mt-1">3 financial scenarios</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <Target className="h-6 w-6 text-green-600 mb-2" />
                <h4 className="font-semibold text-sm text-green-900">Business Intelligence</h4>
                <p className="text-xs text-green-700 mt-1">Break-even & profitability</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Start */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-teal-600" />
              Quick Start Guide
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center font-semibold text-teal-600">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Navigate to Membership Templates</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Click "Membership Templates" in the sidebar under "Services & Billing"
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center font-semibold text-teal-600">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Click "AI Suggestions"</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Find the purple "AI Suggestions" button in the top-right corner
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center font-semibold text-teal-600">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Fill in Your Practice Details</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Enter your specialty (e.g., "Primary Care"), location (e.g., "Austin, TX"), 
                    optional competitor URLs, and select your target market
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center font-semibold text-teal-600">
                  4
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Generate & Review</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Click "Generate Suggestions" and wait 15-20 seconds. Review 3 AI-generated plans 
                    with complete revenue projections
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-teal-100 flex items-center justify-center font-semibold text-teal-600">
                  5
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Save Plans</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    Click "Save All Plans" to add all 3 plans to your membership offerings as drafts
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Understanding Revenue Projections */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Understanding Revenue Projections
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-700">
              Each AI-generated plan includes three financial scenarios to help you plan for different growth outcomes:
            </p>
            
            <div className="space-y-3">
              <div className="border-l-4 border-green-500 pl-4 py-2 bg-green-50">
                <h4 className="font-semibold text-green-900 flex items-center gap-2">
                  Conservative Scenario
                  <Badge className="bg-green-600 text-white border-0">20% Market</Badge>
                </h4>
                <p className="text-sm text-green-700 mt-1">
                  Based on capturing 20% of your target market. This is a safe, risk-averse estimate 
                  with 20 members. Use this for initial planning and budgeting.
                </p>
              </div>

              <div className="border-l-4 border-blue-500 pl-4 py-2 bg-blue-50">
                <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                  Moderate Scenario
                  <Badge className="bg-blue-600 text-white border-0">40% Market</Badge>
                </h4>
                <p className="text-sm text-blue-700 mt-1">
                  Based on capturing 40% of your target market with 40 members. This is the recommended 
                  target for most practices with consistent marketing efforts.
                </p>
              </div>

              <div className="border-l-4 border-purple-500 pl-4 py-2 bg-purple-50">
                <h4 className="font-semibold text-purple-900 flex items-center gap-2">
                  Aggressive Scenario
                  <Badge className="bg-purple-600 text-white border-0">60% Market</Badge>
                </h4>
                <p className="text-sm text-purple-700 mt-1">
                  Based on capturing 60% of your target market with 60 members. This represents maximum 
                  potential with strong marketing and high conversion rates.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Business Intelligence Metrics */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-orange-600" />
              Business Intelligence Metrics Explained
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Break-Even Point</h4>
                <p className="text-sm text-gray-700">
                  The number of members you need to cover your fixed costs ($2,000/month assumed). 
                  This is when your practice becomes profitable.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Year 1 Profit</h4>
                <p className="text-sm text-gray-700">
                  Estimated profit in your first year based on the moderate scenario (40 members), 
                  accounting for 25% variable costs and $2K monthly fixed costs.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">Profit Margin %</h4>
                <p className="text-sm text-gray-700">
                  Your net profitability as a percentage of revenue. Higher margins indicate more 
                  efficient operations. 30%+ is excellent.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">LTV (Lifetime Value)</h4>
                <p className="text-sm text-gray-700">
                  Average revenue per member over their lifetime (18 months assumed). Higher LTV 
                  means more value from each customer.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">CAC (Acquisition Cost)</h4>
                <p className="text-sm text-gray-700">
                  Estimated cost to acquire one new member (50% of monthly price). Lower CAC means 
                  more efficient marketing.
                </p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">LTV:CAC Ratio</h4>
                <p className="text-sm text-gray-700">
                  Ratio of lifetime value to acquisition cost. A ratio of 3:1 or higher indicates 
                  healthy, sustainable unit economics.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Growth Trajectory */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-teal-600" />
              Growth Trajectory Guide
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-gray-700">
              AI provides recommended member targets at 3, 6, and 12-month milestones:
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg">
                <div className="h-10 w-10 rounded-full bg-teal-600 text-white flex items-center justify-center font-semibold">
                  3M
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Month 3: 20 Members</p>
                  <p className="text-sm text-gray-600">Initial launch phase, conservative target</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">
                  6M
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Month 6: 40 Members</p>
                  <p className="text-sm text-gray-600">Growth phase, moderate expansion</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="h-10 w-10 rounded-full bg-purple-600 text-white flex items-center justify-center font-semibold">
                  12M
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Month 12: 60 Members</p>
                  <p className="text-sm text-gray-600">Maturity phase, aggressive target</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Best Practices */}
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-600" />
              Best Practices
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  <strong>Start with Moderate Scenario:</strong> Use the moderate revenue projections 
                  for realistic planning
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  <strong>Compare Multiple Generations:</strong> Generate suggestions 2-3 times with 
                  different inputs to see various options
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  <strong>Edit Before Finalizing:</strong> Plans are saved as drafts - edit pricing 
                  or features before activating
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  <strong>Adjust for Your Market:</strong> Use projections as a guide, but adjust 
                  based on your local market conditions
                </span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-700">
                  <strong>Track Actual vs. Projected:</strong> Compare real results against AI 
                  projections to refine future planning
                </span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Troubleshooting */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              Troubleshooting
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">AI generation failed or timed out</h4>
              <ul className="text-sm text-gray-700 space-y-1 ml-4 list-disc">
                <li>Check your internet connection</li>
                <li>Wait a few moments and try again</li>
                <li>Simplify your specialty description (e.g., "Primary Care" instead of long text)</li>
                <li>Remove competitor URLs if they're causing delays</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Revenue projections seem unrealistic</h4>
              <ul className="text-sm text-gray-700 space-y-1 ml-4 list-disc">
                <li>Projections are based on industry averages - adjust for your specific market</li>
                <li>Consider local cost of living and competition levels</li>
                <li>Use conservative scenario for safer estimates</li>
                <li>Edit plans after saving to match your reality</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Plans not saving to database</h4>
              <ul className="text-sm text-gray-700 space-y-1 ml-4 list-disc">
                <li>Ensure you're logged in with admin credentials</li>
                <li>Check for browser console errors</li>
                <li>Try saving plans individually instead of all at once</li>
                <li>Refresh the page and try again</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Ready to create AI-powered plans?</h3>
                <p className="text-sm text-gray-600">Start generating intelligent membership suggestions now</p>
              </div>
              <Button asChild className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                <Link to="/admin/templates">
                  Go to Templates
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AIMembershipHelp;
